import { calc } from "@ppb/bet-engine";
import { AlertType } from "@ppb/the-wall-common/types";
import { BetslipApplicationState } from "../../store/state/BetslipApplicationState";
import { roundDown, roundUp } from "@ppb/formatters";
import {
  getHasEditedUnmatchedBet,
  getBetslipExchangeReport,
  getIsFreeBetsSelected,
} from "../../store/state/betslip-module/betslip-module-selectors";
import { getExchangePotentialState } from "../../store/state/exchange-betting-potential/exchange-betting-potential-selectors";
import { currencyFormatWithDecimalPlaces } from "../../formatters/currency-adapter";
import type {
  BetslipLocalization,
  BetslipSession,
  ExchangeBetslipCapabilities,
  ExchangeBetslipPreferences,
  ExchangeCurrencyDetails,
} from "../../contract";
import { i18n } from "../../helpers/i18n";
import { buildFreeBetsLabel, computeEachWayExposure, getEachWayReceiptOverrides } from "../betslip-formatters";
import { PlacedBetCardProps } from "./snowflakes/PlacedBetCard/PlacedBetCard.types";
import { ExchangeUnmatchedCardProps } from "./snowflakes/ExchangeUnmatchedCard/ExchangeUnmatchedCard.types";
import type { ExchangeInlineReceiptPanelProps } from "./snowflakes/ExchangeInlineReceiptPanel/ExchangeInlineReceiptPanel.types";
import { getInlineBetslipTitle, isEachWayMarket, isLineBettingType } from "../../helpers/exchange-betslip-helper";
import { isBPEAvailable, shouldDisplayBetWithMatchMe } from "../../helpers/best-price-execution";
import type { ExchangeSide } from "../../types";
import type { BetslipExchangeReport, PlacedBetValues } from "../../store/state/betslip-module/betslip-module.types";

type ReceiptSegmentValues = {
  matched?: ExchangeInlineReceiptPanelProps["matched"];
  unmatched?: ExchangeInlineReceiptPanelProps["unmatched"];
};

type BuildReceiptValuesParams = {
  report: BetslipExchangeReport;
  hasFreeBets: boolean;
  localization: BetslipLocalization;
  isMatchMe: boolean;
  shouldDisplayMatchMeMessage: boolean;
  isEachWayExposureDisplayOn: boolean;
};

type EachWayReceiptContext = {
  selectionId: number;
  bettingType: string;
  eachWayDivisor?: number;
};

type BuildMatchedOrUnmatchedValuesParams = {
  side: ExchangeSide;
  placedBetValues: PlacedBetValues;
  localization: BetslipLocalization;
  hasFreeBets: boolean;
  isCancelled: boolean;
  isMatchMe: boolean;
  marketType?: string;
  eachWayContext?: EachWayReceiptContext;
  shouldDisplayMatchMeMessage?: boolean;
};

const buildStakeDetail = (
  marketType: string | undefined,
  localization: BetslipLocalization,
  size: number,
): string | undefined => {
  if (!marketType || !isEachWayMarket(marketType)) {
    return undefined;
  }

  const multiplierLabel = i18n({ key: "I18N.BETSLIP.2X_MULTIPLIER" });
  const unitStake = currencyFormatWithDecimalPlaces({
    ...localization,
    value: size,
    decimalPlaces: 2,
  });

  return `(${multiplierLabel} ${unitStake})`;
};

/**
 * Round down for a back bet or round up for lay bet so
 * we won't show a better price to the user than the
 * actual price he got.
 */
const getRoundValue = ({ price, side }: { price: number; side: ExchangeSide }): string =>
  side === "BACK" ? roundDown(price).toString() : roundUp(price).toString();

const buildMatchedValues = ({
  side,
  placedBetValues,
  localization,
  hasFreeBets,
  isCancelled,
  isMatchMe,
  marketType,
  eachWayContext,
}: BuildMatchedOrUnmatchedValuesParams): PlacedBetCardProps => {
  const { profit, liability, size, price, totalBonusUsed } = placedBetValues;
  const isLay = side === "LAY";

  const isEachWay = !!(marketType && isEachWayMarket(marketType));

  const eachWayOverrides =
    isEachWay && eachWayContext
      ? getEachWayReceiptOverrides(
          side,
          computeEachWayExposure(
            { side, selectionId: eachWayContext.selectionId, price, size },
            {
              marketType: marketType as string,
              bettingType: eachWayContext.bettingType,
              eachWayDivisor: eachWayContext.eachWayDivisor,
            },
          ),
        )
      : undefined;

  const effectiveProfit = eachWayOverrides?.profit ?? profit ?? 0;
  const effectiveLiability = eachWayOverrides?.liability ?? liability;
  const formattedLiability = effectiveLiability != undefined && effectiveLiability < 0 ? 0 : effectiveLiability;

  const layBetLiability =
    isLay && formattedLiability != undefined
      ? currencyFormatWithDecimalPlaces({ ...localization, value: formattedLiability, decimalPlaces: 2 })
      : undefined;

  const formattedSize = currencyFormatWithDecimalPlaces({
    ...localization,
    value: isEachWay ? 2 * size : size,
    decimalPlaces: 2,
  });

  const stakeDetail = buildStakeDetail(marketType, localization, size);

  const bonus =
    hasFreeBets && totalBonusUsed
      ? buildFreeBetsLabel(
          isCancelled ? "I18N.BETSLIP.CANCELLED_BONUS" : "I18N.BETSLIP.USED_BONUS",
          localization,
          totalBonusUsed,
        )
      : "";

  return {
    titlePrefix: i18n({ key: isLay ? "I18N.BETSLIP.LAY_BET_AGAINST" : "I18N.BETSLIP.BACK_BET_FOR" }),
    type: "MATCHED",
    price: getRoundValue({ price, side }),
    stake: formattedSize,
    stakeDetail,
    profit: currencyFormatWithDecimalPlaces({ ...localization, value: effectiveProfit, decimalPlaces: 2 }),
    liability: layBetLiability,
    bonus,
    hasFreeBets,
    labels: {
      liability: i18n({ key: "I18N.BETSLIP.LIABILITY" }),
      name: i18n({
        key: isMatchMe ? "I18N.BETSLIP.RECEIPT.MATCH_ME" : "I18N.BETSLIP.BET_MATCHED",
      }),
      price: i18n({ key: "I18N.BETSLIP.ODDS" }),
      stake: i18n({ key: isLay ? "I18N.LABELS.BACKER_STAKE" : "I18N.BETSLIP.STAKE" }),
      profit: i18n({ key: "I18N.BETSLIP.PROFIT" }),
    },
  };
};

const buildUnmatchedValues = ({
  side,
  placedBetValues,
  localization,
  hasFreeBets,
  isCancelled,
  isMatchMe,
  marketType,
  eachWayContext,
  shouldDisplayMatchMeMessage,
}: BuildMatchedOrUnmatchedValuesParams): ExchangeUnmatchedCardProps => {
  const commonValues = buildMatchedValues({
    side,
    placedBetValues,
    localization,
    hasFreeBets,
    isCancelled,
    isMatchMe,
    marketType,
    eachWayContext,
  });
  const name = !isCancelled ? i18n({ key: "I18N.BETSLIP.BET_UNMATCHED" }) : undefined;
  const detail =
    !isCancelled && shouldDisplayMatchMeMessage
      ? i18n({ key: "I18N.BETSLIP.BET_UNMATCHED.MATCH_ME_MESSAGE" })
      : undefined;
  const notifications = hasFreeBets
    ? [
        {
          message: i18n({ key: "I18N.BETSLIP.EXC.ERROR.UNABLE_TO_EDIT_BONUS_BET" }),
          detail: i18n({ key: "I18N.BETSLIP.EXC.ERROR.SUBTITLE.UNABLE_TO_EDIT_BONUS_BET" }),
          type: AlertType.Warning,
        },
      ]
    : [];

  return {
    ...commonValues,
    type: "UNMATCHED",
    betId: placedBetValues.betId,
    titlePrefix:
      side === "BACK" ? i18n({ key: "I18N.BETSLIP.BACK_BET_FOR" }) : i18n({ key: "I18N.BETSLIP.LAY_BET_AGAINST" }),
    notifications,
    labels: {
      ...commonValues.labels,
      name,
      detail,
      cancel: i18n({ key: "I18N.BETSLIP.CANCEL_BET" }),
      confirm: i18n({ key: "I18N.BETSLIP.EDIT_BET" }),
    },
  };
};

const buildCancelledValues = (
  side: ExchangeSide,
  placedBetValues: PlacedBetValues,
  localization: BetslipLocalization,
  hasFreeBets: boolean,
  marketType?: string,
  eachWayContext?: EachWayReceiptContext,
): ExchangeUnmatchedCardProps => {
  const commonValues = buildUnmatchedValues({
    side,
    placedBetValues,
    localization,
    hasFreeBets,
    isCancelled: true,
    isMatchMe: false,
    marketType,
    eachWayContext,
  });

  return {
    ...commonValues,
    titlePrefix: i18n({ key: "I18N.BETSLIP.BET_CANCELLED" }),
  };
};

const getEachWayReceiptContext = (report: BetslipExchangeReport): EachWayReceiptContext | undefined => {
  const runnerTree = report.metadata?.runnerTree;
  if (!runnerTree) {
    return undefined;
  }
  const { runner, market } = runnerTree;
  if (!runner || !market) {
    return undefined;
  }
  return {
    selectionId: runner.selectionId,
    bettingType: market.bettingType,
    eachWayDivisor: market.eachWayDivisor,
  };
};

const buildPlacedSegments = ({
  report,
  hasFreeBets,
  localization,
  isMatchMe,
  shouldDisplayMatchMeMessage,
  isEachWayExposureDisplayOn,
}: BuildReceiptValuesParams): ReceiptSegmentValues | undefined => {
  const { side, matched, unmatched } = report;
  const marketType = report.metadata?.runnerTree?.market?.marketType;
  const eachWayContext = isEachWayExposureDisplayOn ? getEachWayReceiptContext(report) : undefined;

  const matchedValues = matched
    ? {
        matched: buildMatchedValues({
          side,
          placedBetValues: matched,
          localization,
          hasFreeBets,
          isCancelled: false,
          isMatchMe,
          marketType,
          eachWayContext,
        }),
      }
    : undefined;
  const unmatchedValues = unmatched
    ? {
        unmatched: buildUnmatchedValues({
          side,
          placedBetValues: unmatched,
          localization,
          hasFreeBets,
          isCancelled: false,
          isMatchMe,
          marketType,
          eachWayContext,
          shouldDisplayMatchMeMessage,
        }),
      }
    : undefined;

  return {
    ...matchedValues,
    ...unmatchedValues,
  };
};

const buildCancelledSegments = (
  report: BetslipExchangeReport,
  hasFreeBets: boolean,
  localization: BetslipLocalization,
  isEachWayExposureDisplayOn: boolean,
): ReceiptSegmentValues | undefined => {
  const { side, cancelled } = report;
  const marketType = report.metadata?.runnerTree?.market?.marketType;
  const eachWayContext = isEachWayExposureDisplayOn ? getEachWayReceiptContext(report) : undefined;

  return cancelled
    ? { unmatched: buildCancelledValues(side, cancelled, localization, hasFreeBets, marketType, eachWayContext) }
    : undefined;
};

const buildReceiptSegmentValues = ({
  report,
  hasFreeBets,
  localization,
  isMatchMe,
  shouldDisplayMatchMeMessage,
  isEachWayExposureDisplayOn,
}: BuildReceiptValuesParams): ReceiptSegmentValues | undefined => {
  const { cancelled } = report;

  return cancelled
    ? buildCancelledSegments(report, hasFreeBets, localization, isEachWayExposureDisplayOn)
    : buildPlacedSegments({
        report,
        hasFreeBets,
        localization,
        isMatchMe,
        shouldDisplayMatchMeMessage,
        isEachWayExposureDisplayOn,
      });
};

function applySpOverlay<T extends { labels: { profit: string } }>(
  segment: T,
  isLay: boolean,
  tbdLabel: string,
  spLabel: string,
): T {
  return {
    ...segment,
    price: spLabel,
    profit: tbdLabel,
    ...(isLay && {
      stake: tbdLabel,
      labels: { ...segment.labels, profit: i18n({ key: "I18N.BETSLIP.PROFIT" }) },
    }),
  } as T;
}

function buildSpSegmentValues(
  isSpActive: boolean,
  side: ExchangeSide,
  report: BetslipExchangeReport,
  localization: BetslipLocalization,
  base: ReceiptSegmentValues | undefined,
): ReceiptSegmentValues | undefined {
  if (!isSpActive) return base;

  const tbdLabel = i18n({ key: "I18N.BETSLIP.TBD" });
  const spLabel = i18n({ key: "I18N.BETSLIP.STARTING_PRICE" });
  const isLay = side === "LAY";

  if (base?.matched) {
    return { ...base, matched: applySpOverlay(base.matched, isLay, tbdLabel, spLabel) };
  }

  if (base?.unmatched) {
    return { ...base, unmatched: applySpOverlay(base.unmatched, isLay, tbdLabel, spLabel) };
  }

  if (report.spSize == null) return base;

  const { spSize, spPriceLimit } = report;
  const formattedSize = currencyFormatWithDecimalPlaces({ ...localization, value: spSize, decimalPlaces: 2 });
  const priceDisplay = spPriceLimit ? String(spPriceLimit) : spLabel;

  let profitDisplay = tbdLabel;
  if (spPriceLimit) {
    const minProfit = calc.spMinProfit(side, spPriceLimit, spSize);
    profitDisplay = currencyFormatWithDecimalPlaces({ ...localization, value: minProfit, decimalPlaces: 2 });
  }

  const spMatchedSegment: PlacedBetCardProps = {
    titlePrefix: i18n({ key: isLay ? "I18N.BETSLIP.LAY_BET_AGAINST" : "I18N.BETSLIP.BACK_BET_FOR" }),
    type: "MATCHED",
    price: priceDisplay,
    stake: isLay ? tbdLabel : formattedSize,
    profit: profitDisplay,
    liability: isLay ? formattedSize : undefined,
    bonus: "",
    hasFreeBets: false,
    labels: {
      liability: i18n({ key: "I18N.BETSLIP.LIABILITY" }),
      name: i18n({ key: "I18N.BETSLIP.RECEIPT_TITLE" }),
      price: spPriceLimit
        ? i18n({ key: isLay ? "I18N.BSP.MAX_ODDS" : "I18N.BSP.MIN_ODDS" })
        : i18n({ key: "I18N.BETSLIP.ODDS" }),
      stake: i18n({ key: isLay ? "I18N.LABELS.BACKER_STAKE" : "I18N.BETSLIP.STAKE" }),
      profit: spPriceLimit ? i18n({ key: "I18N.BSP.MIN_PROFIT" }) : i18n({ key: "I18N.BETSLIP.PROFIT" }),
    },
  };

  return { matched: spMatchedSegment };
}

type BuildExchangeInlineReceiptProps = {
  localization: BetslipLocalization;
  currencyDetails: ExchangeCurrencyDetails;
  session: BetslipSession;
  capabilities: ExchangeBetslipCapabilities;
  preferences: ExchangeBetslipPreferences;
};

/**
 * Builds the props for the exchange inline receipt panel from application state.
 *
 * Returns `undefined` when no exchange report exists.
 *
 * @param appState - The full application state.
 * @param props.localization - Localization supplied by the host through the betslip contract.
 * @param props.currencyDetails - Exchange currency constraints supplied through the betslip contract.
 * @param props.session - Session state supplied by the host through the betslip contract.
 * @param props.capabilities - Exchange capabilities supplied through the betslip contract.
 * @param props.preferences - Exchange preferences supplied through the betslip contract.
 * @returns The receipt panel props, or `undefined` if required data is missing.
 */
export const buildExchangeInlineReceipt = (
  appState: BetslipApplicationState,
  { localization, currencyDetails, session, capabilities, preferences }: BuildExchangeInlineReceiptProps,
): ExchangeInlineReceiptPanelProps | undefined => {
  const report = getBetslipExchangeReport(appState);

  if (!report) {
    return undefined;
  }

  const { runner, side } = report;
  const isMatchMe =
    preferences.matchMe.enabled &&
    isBPEAvailable(appState, session, capabilities, preferences) &&
    !getHasEditedUnmatchedBet(appState);
  const shouldDisplayMatchMeMessage = shouldDisplayBetWithMatchMe(
    appState,
    currencyDetails,
    session,
    capabilities,
    preferences,
  );
  const hasFreeBets = getIsFreeBetsSelected(appState);
  const title = getInlineBetslipTitle(appState);
  const isSpActive = !!getExchangePotentialState(appState, runner, side)?.isSp;
  const baseSegmentValues = buildReceiptSegmentValues({
    report,
    hasFreeBets,
    localization,
    isMatchMe,
    shouldDisplayMatchMeMessage,
    isEachWayExposureDisplayOn: capabilities.eachWayExposureDisplay.enabled,
  });
  const segmentValues = buildSpSegmentValues(isSpActive, side, report, localization, baseSegmentValues);

  return {
    title,
    side,
    isLineBettingType: isLineBettingType(report.metadata?.runnerTree?.market?.bettingType),
    ...segmentValues,
  };
};
